import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { featuresOf } from '../../lib/features'
import { photoFailure, shrinkPhoto } from '../../lib/photo'
import { removePhoto, uploadPhoto } from '../../services/photo'
import UserPhoto from './UserPhoto'

const NOTICE_TONE = {
  good: 'bg-good-tint text-good-ink',
  muted: 'bg-surface-2 text-ink-2',
  bad: 'bg-bad-tint text-bad-ink',
  warn: 'bg-warn-tint text-warn-ink',
}

/**
 * The profile photo on the edit page (V5-w): choose, shrink on a canvas to a
 * 512×512 JPEG, upload (`me_uploadPhoto`); remove (`me_removePhoto`).
 *
 * Invisible while photos are switched off (`social.photosEnabled`,
 * production until stage 3): the upload control shows only when the
 * profile's `features.photosEnabled` is true (V5-w-flags) — no request is
 * made to find out. A learner who already has a photo still sees it with
 * «حذف عکس» — removing works either way. A real upload refused with
 * `photos_disabled` (switched off since the profile loaded) hides the
 * control and says so in a neutral line.
 */
export default function ProfilePhotoField({ user, onChanged }) {
  const { t } = useTranslation('editProfile')
  const [refused, setRefused] = useState(false)
  const [busy, setBusy] = useState(null)
  const [notice, setNotice] = useState(null)
  const input = useRef(null)

  const photoUrl = user?.photoUrl ?? null
  const canUpload = featuresOf(user).photosEnabled && !refused
  if (!canUpload && !photoUrl) {
    return notice ? (
      <p role="status" className={`rounded-xl px-3 py-2 text-xs ${NOTICE_TONE[notice.tone]}`}>
        {notice.text}
      </p>
    ) : null
  }

  async function choose(event) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || busy) return
    setNotice(null)
    setBusy('upload')
    let blob
    try {
      blob = await shrinkPhoto(file)
    } catch {
      setBusy(null)
      setNotice({ tone: 'warn', text: t('photo.failure.unreadable') })
      return
    }
    try {
      onChanged(await uploadPhoto(blob))
      setNotice({ tone: 'good', text: t('photo.uploaded') })
    } catch (error) {
      const failure = photoFailure(error)
      if (failure === 'disabled') {
        setRefused(true)
        setNotice({ tone: 'muted', text: t('photo.failure.disabled') })
      } else {
        setNotice({ tone: failure === 'offline' ? 'warn' : 'bad', text: t(`photo.failure.${failure}`) })
      }
    } finally {
      setBusy(null)
    }
  }

  async function remove() {
    if (busy) return
    setNotice(null)
    setBusy('remove')
    try {
      onChanged(await removePhoto())
      setNotice({ tone: 'good', text: t('photo.removed') })
    } catch (error) {
      const failure = photoFailure(error)
      setNotice({ tone: failure === 'offline' ? 'warn' : 'bad', text: t(`photo.failure.${failure === 'disabled' ? 'service' : failure}`) })
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="flex flex-col gap-2" data-photo-field={canUpload ? 'on' : 'remove-only'}>
      <span className="text-[12.5px] font-semibold text-ink-2">{t('photo.label')}</span>
      <div className="flex items-center gap-3 rounded-[14px] border-[1.5px] border-line bg-surface p-3">
        <UserPhoto photoUrl={photoUrl} size={56}>
          <span aria-hidden="true" className="grid size-14 shrink-0 place-items-center rounded-full border-[2.5px] border-brand-600 bg-brand-50 text-2xl">
            🦜
          </span>
        </UserPhoto>
        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
          {canUpload && (
            <>
              <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={choose} aria-hidden="true" tabIndex={-1} />
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => input.current?.click()}
                className="h-9 rounded-full bg-brand-600 px-3.5 text-[12px] font-bold text-white disabled:opacity-60 dark:text-slate-900"
              >
                {busy === 'upload' ? t('photo.uploading') : photoUrl ? t('photo.change') : t('photo.choose')}
              </button>
            </>
          )}
          {photoUrl && (
            <button
              type="button"
              disabled={busy !== null}
              onClick={remove}
              className="h-9 rounded-full bg-surface-2 px-3.5 text-[12px] font-bold text-bad-ink disabled:opacity-60"
            >
              {busy === 'remove' ? t('photo.removing') : t('photo.remove')}
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] leading-[1.85] text-ink-3">{t('photo.helper')}</p>
      {notice && (
        <p role="status" className={`rounded-xl px-3 py-2 text-xs ${NOTICE_TONE[notice.tone]}`}>
          {notice.text}
        </p>
      )}
    </section>
  )
}
